import React, { useState, useEffect, useCallback } from 'react';
import {
  processLeadOutcome,
  processSpeedToLead,
  processCallRail,
  computeMoneyMetrics,
  formatMinutes,
} from './utils/process';
import { fetchAllData } from './utils/sheets';
import './App.css';

// ─── ANIMATED NUMBER ─────────────────────────
function AnimNum({ value, prefix = '', suffix = '', decimals = 0 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const dur = 1400;
    const animate = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setDisplay(ease * value);
      if (p < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return (
    <span>
      {prefix}
      {decimals > 0 ? display.toFixed(decimals) : Math.round(display).toLocaleString()}
      {suffix}
    </span>
  );
}

// ─── RING GAUGE ──────────────────────────────
function RingGauge({ value, size = 140, stroke = 10, color = 'var(--accent)' }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (value / 100) * circ;
  return (
    <div className="ring-gauge" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--border)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1.5s cubic-bezier(0.22,1,0.36,1)' }} />
      </svg>
      <div className="ring-label">
        <span className="ring-value"><AnimNum value={value} decimals={1} suffix="%" /></span>
      </div>
    </div>
  );
}

// ─── BAR (horizontal) ────────────────────────
function HBar({ label, value, max, color = 'var(--accent)' }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="hbar">
      <div className="hbar-header">
        <span className="hbar-label">{label}</span>
        <span className="hbar-value" style={{ color }}>{typeof value === 'number' ? value.toLocaleString() : value}</span>
      </div>
      <div className="hbar-track">
        <div className="hbar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

// ─── LEADS OVER TIME CHART ───────────────────
function LeadsOverTimeChart({ data }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const CASE_TYPE_MAP = { 'Auto Accident': 'MVA' };
  const mapCT = (t) => CASE_TYPE_MAP[t] || t;

  // Group by week
  const weeks = {};
  data.forEach(r => {
    const dateStr = r['Lead Status Changed'] || '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return;
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const ws = new Date(d);
    ws.setDate(diff);
    const key = ws.toISOString().split('T')[0];
    if (!weeks[key]) weeks[key] = 0;
    weeks[key]++;
  });

  const sorted = Object.entries(weeks).sort((a, b) => a[0].localeCompare(b[0]));
  if (sorted.length === 0) return <p className="no-data-msg">No data for selected period</p>;

  const values = sorted.map(([, v]) => v);
  const maxVal = Math.max(...values, 1);
  const chartH = 160;

  return (
    <div className="leads-chart-wrap">
      <svg viewBox={`0 0 ${sorted.length * 40} ${chartH + 30}`} className="leads-chart-svg">
        <defs>
          <linearGradient id="leadsGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.25" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Area fill */}
        <polygon
          fill="url(#leadsGrad)"
          points={sorted.map(([, v], i) => {
            const x = i * 40 + 20;
            const y = chartH - (v / maxVal) * (chartH - 20);
            return `${x},${y}`;
          }).join(' ') + ` ${(sorted.length - 1) * 40 + 20},${chartH} 20,${chartH}`}
        />
        {/* Line */}
        <polyline
          fill="none"
          stroke="var(--accent)"
          strokeWidth="2.5"
          strokeLinejoin="round"
          points={sorted.map(([, v], i) => {
            const x = i * 40 + 20;
            const y = chartH - (v / maxVal) * (chartH - 20);
            return `${x},${y}`;
          }).join(' ')}
        />
        {/* Dots + hover zones */}
        {sorted.map(([week, v], i) => {
          const x = i * 40 + 20;
          const y = chartH - (v / maxVal) * (chartH - 20);
          const isHovered = hoveredIdx === i;
          return (
            <g key={week}>
              <rect x={x - 18} y={0} width={36} height={chartH + 30} fill="transparent"
                onMouseEnter={() => setHoveredIdx(i)} onMouseLeave={() => setHoveredIdx(null)} />
              <circle cx={x} cy={y} r={isHovered ? 6 : 3} fill="var(--accent)" opacity={isHovered ? 1 : 0.7}
                style={{ transition: 'r 0.15s' }} />
              {isHovered && (
                <g>
                  <rect x={x - 40} y={y - 36} width={80} height={26} rx={6} fill="var(--surface-hover)" stroke="var(--border-light)" strokeWidth="1" />
                  <text x={x} y={y - 19} textAnchor="middle" fill="var(--text)" fontSize="12" fontWeight="700" fontFamily="'DM Sans'">{v} leads</text>
                </g>
              )}
              <text x={x} y={chartH + 16} textAnchor="middle" fill="var(--text-dim)" fontSize="9" fontFamily="'IBM Plex Mono'">{week.slice(5)}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─── FUNNEL ──────────────────────────────────
function Funnel({ stages }) {
  const max = stages[0]?.value || 1;
  const colors = ['var(--accent)', 'var(--purple)', 'var(--amber)', 'var(--green)'];
  return (
    <div className="funnel">
      {stages.map((s, i) => {
        const pct = (s.value / max) * 100;
        const dropoff = i > 0
          ? (((stages[i - 1].value - s.value) / stages[i - 1].value) * 100).toFixed(1)
          : null;
        return (
          <div key={s.label} className="funnel-stage" style={{ animationDelay: `${i * 0.15}s` }}>
            <div className="funnel-stage-header">
              <span className="funnel-stage-label">{s.label}</span>
              <div className="funnel-stage-stats">
                <span className="funnel-stage-value">{s.value}</span>
                {dropoff && <span className="funnel-drop">-{dropoff}%</span>}
              </div>
            </div>
            <div className="hbar-track">
              <div className="hbar-fill" style={{ width: `${pct}%`, background: colors[i % colors.length] }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── METRIC CARD ─────────────────────────────
function MetricCard({ label, value, prefix = '', suffix = '', sub, accent = 'var(--accent)', decimals = 0 }) {
  return (
    <div className="metric-card">
      <div className="metric-card-accent" style={{ background: accent }} />
      <span className="metric-label">{label}</span>
      <span className="metric-value">
        <AnimNum value={value} prefix={prefix} suffix={suffix} decimals={decimals} />
      </span>
      {sub && <span className="metric-sub">{sub}</span>}
    </div>
  );
}

// ─── DATE FILTER ─────────────────────────────
function DateFilter({ range, onChange, customStart, customEnd, onCustomChange }) {
  return (
    <div className="date-filter">
      {['all', '7d', '30d', '90d', '6m', '1y'].map(r => (
        <button key={r} className={`date-btn ${range === r ? 'active' : ''}`} onClick={() => onChange(r)}>
          {r === 'all' ? 'All' : r === '6m' ? '6mo' : r === '1y' ? '1yr' : r}
        </button>
      ))}
      <button className={`date-btn ${range === 'custom' ? 'active' : ''}`} onClick={() => onChange('custom')}>
        Custom
      </button>
      {range === 'custom' && (
        <div className="custom-dates">
          <input type="date" className="date-input" value={customStart} onChange={e => onCustomChange('start', e.target.value)} />
          <span className="date-to">to</span>
          <input type="date" className="date-input" value={customEnd} onChange={e => onCustomChange('end', e.target.value)} />
        </div>
      )}
    </div>
  );
}

// ─── CASE TYPE FILTER ────────────────────────
function CaseTypeFilter({ types, selected, onChange }) {
  return (
    <div className="case-filter">
      <button className={`case-btn ${selected === 'all' ? 'active' : ''}`} onClick={() => onChange('all')}>All</button>
      {types.map(t => (
        <button key={t} className={`case-btn ${selected === t ? 'active' : ''}`} onClick={() => onChange(t)}>
          {t}
        </button>
      ))}
    </div>
  );
}

// ─── CASE TYPE TREND ─────────────────────────
function CaseTypeTrend({ data }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const CASE_TYPE_MAP = { 'Auto Accident': 'MVA' };
  const mapCaseType = (t) => CASE_TYPE_MAP[t] || t;

  const weeks = {};
  data.forEach(r => {
    const dateStr = r['Lead Status Changed'] || '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return;
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const ws = new Date(d);
    ws.setDate(diff);
    const key = ws.toISOString().split('T')[0];
    const ct = mapCaseType(r['Case Type'] || 'Other');
    if (!weeks[key]) weeks[key] = {};
    weeks[key][ct] = (weeks[key][ct] || 0) + 1;
  });

  const sortedWeeks = Object.entries(weeks).sort((a, b) => a[0].localeCompare(b[0]));
  if (sortedWeeks.length === 0) return <p className="no-data-msg">No data for selected period</p>;

  const targetTypes = ['MVA', 'Premises Liability'];
  const allTypes = [...new Set(data.map(r => mapCaseType(r['Case Type'] || 'Other')))];
  const otherTypes = allTypes.filter(t => !targetTypes.includes(t));

  const typeColors = {
    'MVA': 'var(--accent)',
    'Premises Liability': 'var(--purple)',
    'Workers Comp': 'var(--amber)',
    'Medical Malpractice': 'var(--red)',
  };
  const getColor = (type, i) => typeColors[type] || `hsl(${(i * 47) % 360}, 50%, 55%)`;
  const maxTotal = Math.max(...sortedWeeks.map(([, types]) => Object.values(types).reduce((a, b) => a + b, 0)), 1);

  return (
    <div>
      <div className="trend-legend">
        {[...targetTypes, ...otherTypes].map((type, i) => (
          <span key={type} className="trend-legend-item">
            <span className="trend-dot" style={{ background: getColor(type, i) }} />
            {type}
          </span>
        ))}
      </div>
      <div className="trend-chart">
        {sortedWeeks.map(([week, types], idx) => {
          const total = Object.values(types).reduce((a, b) => a + b, 0);
          const targetCount = (types['MVA'] || 0) + (types['Premises Liability'] || 0);
          const targetPct = ((targetCount / total) * 100).toFixed(0);
          const isHovered = hoveredIdx === idx;
          return (
            <div key={week} className="trend-col"
              onMouseEnter={() => setHoveredIdx(idx)} onMouseLeave={() => setHoveredIdx(null)}>
              {isHovered && (
                <div className="trend-tooltip">
                  <div className="trend-tooltip-title">{week}</div>
                  {[...targetTypes, ...otherTypes].map((type, i) => {
                    const val = types[type] || 0;
                    if (val === 0) return null;
                    return <div key={type} className="trend-tooltip-row"><span style={{ color: getColor(type, i) }}>{type}:</span> {val}</div>;
                  })}
                  <div className="trend-tooltip-total">Total: {total}</div>
                </div>
              )}
              <div className="trend-bar-stack" style={{ height: `${(total / maxTotal) * 100}%` }}>
                {[...targetTypes, ...otherTypes].map((type, i) => {
                  const val = types[type] || 0;
                  if (val === 0) return null;
                  const pct = (val / total) * 100;
                  return (
                    <div key={type} className="trend-bar-seg" style={{
                      height: `${pct}%`,
                      background: getColor(type, i),
                      opacity: targetTypes.includes(type) ? 1 : 0.4,
                    }} />
                  );
                })}
              </div>
              <span className="trend-week-label">{week.slice(5)}</span>
              <span className="trend-target-pct" style={{
                color: parseInt(targetPct) >= 70 ? 'var(--green)' : 'var(--text-dim)'
              }}>{targetPct}%</span>
            </div>
          );
        })}
      </div>
      <div className="trend-note">
        <span className="trend-note-text">% = MVA + Premises Liability share of total leads per week</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════
function App() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [caseTypeFilter, setCaseTypeFilter] = useState('all');
  const [lastUpdated, setLastUpdated] = useState(null);

  const handleCustomDateChange = (which, val) => {
    if (which === 'start') setCustomStart(val);
    else setCustomEnd(val);
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const raw = await fetchAllData();
      setData(raw);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error('Data fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadData]);

  if (loading && !data) {
    return (
      <div className="loading-screen">
        <div className="loader" />
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="loading-screen">
        <p className="error-msg">Failed to load data: {error}</p>
        <button className="retry-btn" onClick={loadData}>Retry</button>
      </div>
    );
  }

  // ─── FILTER DATA ────────────────────────────
  const filterByDate = (rows, dateField) => {
    if (dateRange === 'all') return rows;

    if (dateRange === 'custom') {
      const start = customStart ? new Date(customStart + 'T00:00:00') : null;
      const end = customEnd ? new Date(customEnd + 'T23:59:59') : null;
      return rows.filter(r => {
        const raw = r[dateField] || '';
        const d = new Date(raw.replace('T05:00:00.000Z', ''));
        if (isNaN(d.getTime())) return false;
        if (start && d < start) return false;
        if (end && d > end) return false;
        return true;
      });
    }

    const now = new Date();
    const dayMap = { '7d': 7, '30d': 30, '90d': 90, '6m': 180, '1y': 365 };
    const days = dayMap[dateRange] || 30;
    const cutoff = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    return rows.filter(r => {
      const raw = r[dateField] || '';
      const d = new Date(raw.replace('T05:00:00.000Z', ''));
      return !isNaN(d.getTime()) && d >= cutoff;
    });
  };

  const filterByCaseType = (rows, field = 'Case Type') => {
    if (caseTypeFilter === 'all') return rows;
    const REVERSE_MAP = { 'MVA': 'Auto Accident' };
    const originalName = REVERSE_MAP[caseTypeFilter] || caseTypeFilter;
    return rows.filter(r => r[field] === originalName || r[field] === caseTypeFilter);
  };

  // Apply filters — exclude Error/Duplicate from lead outcome
  const cleanLeadOutcome = data.leadOutcome.filter(r => r.Status !== 'Error/Duplicate');
  const dateFilteredLeadOutcome = filterByDate(cleanLeadOutcome, 'Lead Status Changed');
  const filteredLeadOutcome = filterByCaseType(dateFilteredLeadOutcome);
  const filteredSpend = filterByDate(data.spend, 'Date');
  const filteredSpeedToLead = filterByCaseType(filterByDate(data.speedToLead, 'Inbound Date'));
  const filteredCallRail = filterByDate(data.callRail, 'Start Time');

  // Process — money metrics always use ALL leads (date filtered only, no case type filter)
  const money = computeMoneyMetrics(filteredSpend, dateFilteredLeadOutcome);
  const leadOutcome = processLeadOutcome(filteredLeadOutcome);
  const stl = processSpeedToLead(filteredSpeedToLead);
  const calls = processCallRail(filteredCallRail);

  // Get unique case types for filter — rename Auto Accident to MVA
  const CASE_TYPE_MAP = { 'Auto Accident': 'MVA' };
  const mapCaseType = (t) => CASE_TYPE_MAP[t] || t;
  const allCaseTypes = [...new Set(cleanLeadOutcome.map(r => mapCaseType(r['Case Type'])).filter(Boolean))].sort();

  // Funnel stages
  const funnelStages = [
    { label: 'Leads In', value: leadOutcome.totalLeads },
    { label: 'In Intake', value: leadOutcome.intake + leadOutcome.signed },
    { label: 'Signed (Treating)', value: leadOutcome.signed },
  ];

  return (
    <div className="app">
      {/* ─── HEADER ─── */}
      <header className="header">
        <div className="header-left">
          <div className="logo">
            <img src="/logo.png" alt="Fiore Legal" className="logo-img" />
            <span className="logo-divider" />
            <span className="logo-sub">Intake Performance Dashboard</span>
          </div>
        </div>
        <div className="header-right">
          <div className="live-badge">
            <span className="live-dot" />
            Live
          </div>
          {lastUpdated && (
            <span className="last-updated">
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button className="refresh-btn" onClick={loadData} disabled={loading}>
            ↻ Refresh
          </button>
        </div>
      </header>

      {/* ─── FILTERS ─── */}
      <div className="filters-bar">
        <DateFilter range={dateRange} onChange={setDateRange} customStart={customStart} customEnd={customEnd} onCustomChange={handleCustomDateChange} />
        <CaseTypeFilter types={allCaseTypes} selected={caseTypeFilter} onChange={setCaseTypeFilter} />
      </div>

      {/* ─── MONEY METRICS ─── */}
      <section className="section">
        <div className="section-header">
          <h2>Money Metrics</h2>
          <span className="section-sub">What your Facebook spend is actually producing</span>
        </div>
        <div className="metrics-grid">
          <MetricCard label="Total FB Spend" value={money.totalSpend} prefix="$" accent="var(--red)" />
          <MetricCard label="Total Leads" value={money.totalLeads} accent="var(--accent)" />
          <MetricCard label="Cost Per Lead" value={money.costPerLead} prefix="$" decimals={2} accent="var(--amber)" />
          <MetricCard label="Signed Cases" value={money.signed} accent="var(--green)" sub={`${money.signRate.toFixed(1)}% sign rate`} />
          <MetricCard label="Cost Per Signed" value={money.costPerSigned} prefix="$" decimals={0} accent="var(--red)" />
          <MetricCard label="Total Clicks" value={money.totalClicks} accent="var(--purple)" />
        </div>
      </section>

      {/* ─── LEADS OVER TIME ─── */}
      <section className="section">
        <div className="section-header">
          <h2>Leads Over Time</h2>
          <span className="section-sub">Weekly lead volume</span>
        </div>
        <div className="panel">
          <LeadsOverTimeChart data={dateFilteredLeadOutcome} />
        </div>
      </section>

      {/* ─── SPEED TO LEAD ─── */}
      <section className="section">
        <div className="section-header">
          <h2>Speed to Lead</h2>
          <span className="section-sub">Time from inquiry to first human contact</span>
        </div>
        <div className="grid-2">
          <div className="panel">
            <div className="stl-hero">
              <div className="stl-hero-number">
                {formatMinutes(stl.avgTimeMinutes)}
              </div>
              <div className="stl-hero-label">Avg. Time to Contact</div>
            </div>
            <div className="stl-stats">
              <div className="stl-stat">
                <span className="stl-stat-label">Contact Rate</span>
                <span className="stl-stat-value" style={{ color: stl.contactRate > 80 ? 'var(--green)' : 'var(--amber)' }}>
                  {stl.contactRate.toFixed(1)}%
                </span>
                <span className="stl-stat-detail">{stl.contacted} / {stl.totalLeads} leads</span>
              </div>
              <div className="stl-stat">
                <span className="stl-stat-label">In Hours (8-5)</span>
                <span className="stl-stat-value">{formatMinutes(stl.avgInHours)}</span>
                <span className="stl-stat-detail">{stl.inHoursCount} leads</span>
              </div>
              <div className="stl-stat">
                <span className="stl-stat-label">Off Hours</span>
                <span className="stl-stat-value" style={{ color: 'var(--amber)' }}>{formatMinutes(stl.avgOutHours)}</span>
                <span className="stl-stat-detail">{stl.outHoursCount} leads</span>
              </div>
              <div className="stl-stat">
                <span className="stl-stat-label">Weekday Avg</span>
                <span className="stl-stat-value">{formatMinutes(stl.avgWeekday)}</span>
              </div>
              <div className="stl-stat">
                <span className="stl-stat-label">Weekend Avg</span>
                <span className="stl-stat-value" style={{ color: 'var(--red)' }}>{formatMinutes(stl.avgWeekend)}</span>
              </div>
            </div>
          </div>
          <div className="panel">
            <h3 className="panel-title">By Case Type</h3>
            {stl.caseTypeBreakdown.map(ct => (
              <div key={ct.type} className="stl-casetype">
                <div className="stl-casetype-header">
                  <span className="stl-casetype-name">{ct.type}</span>
                  <span className="stl-casetype-count">{ct.total} leads</span>
                </div>
                <div className="stl-casetype-stats">
                  <span>Avg: {ct.avgTime !== null ? formatMinutes(ct.avgTime) : '—'}</span>
                  <span>Contacted: {ct.contacted}/{ct.total}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── LEAD OUTCOME / INTAKE FUNNEL ─── */}
      <section className="section">
        <div className="section-header">
          <h2>Intake Funnel</h2>
          <span className="section-sub">Where cases die between ad click and retainer</span>
        </div>
        <div className="grid-2">
          <div className="panel">
            <h3 className="panel-title">Pipeline</h3>
            <Funnel stages={funnelStages} />
          </div>
          <div className="panel">
            <h3 className="panel-title">Case Type Breakdown</h3>
            {leadOutcome.caseTypeBreakdown.slice(0, 8).map(ct => (
              <HBar
                key={ct.type}
                label={ct.type}
                value={ct.count}
                max={leadOutcome.caseTypeBreakdown[0]?.count || 1}
                color="var(--accent)"
              />
            ))}
          </div>
        </div>
        <div className="panel" style={{ marginTop: 20 }}>
          <h3 className="panel-title">Status Breakdown</h3>
          <div className="status-grid">
            {Object.entries(leadOutcome.statuses).map(([status, count]) => {
              const colorMap = {
                Treating: 'var(--green)',
                Rejected: 'var(--red)',
                Intake: 'var(--amber)',
              };
              return (
                <div key={status} className="status-chip" style={{ borderColor: colorMap[status] || 'var(--border)' }}>
                  <span className="status-chip-count" style={{ color: colorMap[status] || 'var(--text-muted)' }}>{count}</span>
                  <span className="status-chip-label">{status}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── CASE TYPE TREND ─── */}
      <section className="section">
        <div className="section-header">
          <h2>Case Type Trend</h2>
          <span className="section-sub">Tracking optimization toward MVA and Premises Liability</span>
        </div>
        <div className="panel">
          <CaseTypeTrend data={dateFilteredLeadOutcome} />
        </div>
      </section>

      {/* ─── CALL PERFORMANCE ─── */}
      <section className="section">
        <div className="section-header">
          <h2>Call Performance</h2>
          <span className="section-sub">After-hours is where PI firms lose the most cases</span>
        </div>
        <div className="grid-3">
          <div className="panel center">
            <h3 className="panel-title">Answer Rate</h3>
            <RingGauge
              value={calls.answerRate}
              color={calls.answerRate >= 80 ? 'var(--green)' : calls.answerRate >= 60 ? 'var(--amber)' : 'var(--red)'}
            />
            <div className="ring-stats">
              <span><strong>{calls.answered}</strong> answered</span>
              <span><strong>{calls.missed}</strong> missed</span>
            </div>
          </div>
          <div className="panel center">
            <h3 className="panel-title">Total Calls</h3>
            <div className="big-number">{calls.totalCalls}</div>
            <div className="ring-stats">
              <span><strong>{calls.firstTimers}</strong> first-time callers</span>
              <span>Avg duration: <strong>{Math.round(calls.avgDuration)}s</strong></span>
            </div>
          </div>
          <div className="panel center">
            <h3 className="panel-title">After Hours</h3>
            <div className="big-number" style={{ color: 'var(--amber)' }}>{calls.afterHoursCalls}</div>
            <div className="ring-stats">
              <span>{calls.afterHoursPct.toFixed(1)}% of all calls</span>
              <span>Before 8am / After 5pm</span>
            </div>
          </div>
        </div>

        <div className="grid-2" style={{ marginTop: 20 }}>
          <div className="panel">
            <h3 className="panel-title">Missed Calls by Hour</h3>
            <div className="hourly-chart">
              {calls.missedByHour.filter(h => h.hour >= 6 && h.hour <= 22).map(h => {
                const maxMissed = Math.max(...calls.missedByHour.map(x => x.missed), 1);
                const pct = (h.missed / maxMissed) * 100;
                const isAfterHours = h.hour < 8 || h.hour >= 17;
                return (
                  <div key={h.hour} className="hourly-bar-wrap">
                    <div className="hourly-bar"
                      style={{
                        height: `${Math.max(pct, 4)}%`,
                        background: isAfterHours ? 'var(--red)' : 'var(--accent)',
                        opacity: h.missed > 0 ? 1 : 0.2,
                      }}
                    />
                    <span className="hourly-label">{h.label}</span>
                    {h.missed > 0 && <span className="hourly-count">{h.missed}</span>}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="panel">
            <h3 className="panel-title">Calls by Source</h3>
            {calls.sourceBreakdown.slice(0, 8).map(s => (
              <div key={s.source} className="source-row">
                <span className="source-name">{s.source}</span>
                <div className="source-stats">
                  <span className="source-total">{s.total}</span>
                  <span className="source-rate" style={{
                    color: (s.answered / (s.total || 1)) > 0.8 ? 'var(--green)' : 'var(--amber)'
                  }}>
                    {((s.answered / (s.total || 1)) * 100).toFixed(0)}% ans
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="footer">
        <span>Data refreshes every 5 minutes</span>
      </footer>
    </div>
  );
}

export default App;